import { DAILY_NAME } from './constants';
import type { MediaRef, MessageDoc } from './types';

/**
 * What a message is, in a few words, wherever it is shown by something other
 * than itself: a notification, the quote over a reply, the pinned bar.
 *
 * One place, because they drifted apart. A notification said "sent a voice
 * note" while the quote of the same message said "a photo", and a reply to a
 * poll or a location quoted nothing at all.
 */

type Summarised = Pick<MessageDoc, 'kind' | 'text' | 'media' | 'poll'> & Partial<Pick<MessageDoc, 'deletedAt'>>;

/** The kind of thing attached, the most particular first. */
export function attachmentOf(media: readonly MediaRef[] | undefined): 'file' | 'voice note' | 'video' | 'photo' {
  const kinds = new Set((media ?? []).map((each) => each.kind));
  if (kinds.has('file')) return 'file';
  if (kinds.has('audio')) return 'voice note';
  if (kinds.has('video')) return 'video';
  return 'photo';
}

/**
 * The message as a quote: its words where it has any, otherwise what it is.
 * A file goes by its name, which is the whole of what there is to it.
 */
export function summaryOf(message: Summarised): string {
  // Its words and pictures go with it, so a photo would otherwise be quoted as
  // "a photo" -- the one thing it no longer is. The chat no longer draws the
  // deleted row itself, so a reply's quote is where it is still named.
  if (message.deletedAt) return 'Deleted message';
  const text = (message.text ?? '').trim();
  switch (message.kind) {
    case 'media': {
      if (text) return text;
      const what = attachmentOf(message.media);
      if (what === 'file') return message.media?.find((m) => m.kind === 'file')?.name || 'a file';
      return `a ${what}`;
    }
    case 'poll':
      return message.poll?.question?.trim() || 'a poll';
    case 'location':
      return 'a location';
    case 'snap':
      return 'a snap';
    case 'dailyPointer':
      return `today’s ${DAILY_NAME}`;
    default:
      return text;
  }
}

/**
 * The picture that stands for a message, shown small beside its summary: a
 * photo, or a clip's poster. None for a snap, which is for looking at once,
 * for a deleted message, or for anything with nothing to look at.
 */
export function pictureOf(message: Pick<MessageDoc, 'kind' | 'media' | 'deletedAt'>): MediaRef | null {
  if (message.kind !== 'media' || message.deletedAt) return null;
  const media = message.media ?? [];
  return media.find((m) => m.kind === 'photo') ?? media.find((m) => m.kind === 'poster') ?? null;
}
